/**
 * Stripe から戻ってきたことを、React が動く前に記録する。
 *
 * なぜこの形か:
 *   App の初期化は `history.replaceState(..., currentPath)` を
 *   **pathname だけ**で呼ぶため（App.tsx の初期 view 決定）、
 *   `?success=true` は最初の描画より前に URL から消える。
 *   画面側の useEffect で読もうとしても、そのときには何も残っていない。
 *
 *   さらにこの画面はログインの復元中に一度描かれて作り直されることがあり、
 *   コンポーネントの state に置くと最初のマウントごと捨てられる。
 *
 *   よって「React より前に読む」「タブ内で生き残る場所に置く」の両方が要る。
 *   sessionStorage はタブを閉じれば消え、他のタブにも影響しない。
 */

const KEY = 'anzen:checkoutReturn';

export type CheckoutReturn = 'success' | 'canceled';

/** 起動時に1回だけ呼ぶ。URL のクエリを見て、戻ってきた種別を控える。 */
export function captureCheckoutReturn(): void {
  if (typeof window === 'undefined') return;
  try {
    const q = new URLSearchParams(window.location.search);
    const kind: CheckoutReturn | null =
      q.get('success') === 'true' ? 'success'
      : q.get('canceled') === 'true' ? 'canceled'
      : null;
    if (!kind) return;
    /* 時刻も持たせる。持たせないと、以前「中止」で戻ったときの印が
       タブに残り続け、**後で成功して戻ったときに「中止しました」と
       表示される**（実際に発生）。決済の往復は長くても数分なので、
       古い印は無効として扱う。 */
    sessionStorage.setItem(KEY, JSON.stringify({ kind, at: Date.now() }));
    /* クエリは消す。残すと再読み込みのたびに同じ案内が出る。
       hash は触らない（マジックリンクの処理がまだ使う）。 */
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.hash,
    );
  } catch {
    /* sessionStorage が使えない設定でも起動は止めない */
  }
}

/** 控えてある種別を読む。無ければ null。 */
/** 印が有効な時間。決済の往復にこれ以上かかることはない。 */
const MAX_AGE_MS = 10 * 60 * 1000;

export function readCheckoutReturn(): CheckoutReturn | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { kind?: unknown; at?: unknown };
    const kind = v?.kind === 'success' || v?.kind === 'canceled' ? v.kind : null;
    if (!kind) { sessionStorage.removeItem(KEY); return null; }
    if (typeof v.at !== 'number' || Date.now() - v.at > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return kind;
  } catch {
    /* 形が違う（旧形式の文字列など）ときは消して無かったことにする。 */
    try { sessionStorage.removeItem(KEY); } catch { /* noop */ }
    return null;
  }
}

/** 案内を出し終えたら消す。 */
export function clearCheckoutReturn(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
